import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/i18n/LanguageProvider';
import { SITE_LANGS, SITE_LANG_CODES, SITE_LANG_HINTS, SITE_LANG_NAMES } from '@/i18n/meta';
import BrandLogo from '@/components/brand/BrandLogo';
import UserAccountMenu from '@/components/brand/UserAccountMenu';
import { appUrl } from '@/config/hosts';

function GlobeIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="9.5" />
      <path d="M2.5 12h19M12 2.5c2.6 2.7 4 6 4 9.5s-1.4 6.8-4 9.5c-2.6-2.7-4-6-4-9.5s1.4-6.8 4-9.5z" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg className="vani-lang-chevron" width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M2.2 4.2 6 8l3.8-3.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LangSwitch() {
  const { lang, setLang, t } = useLanguage();
  const currentLang = SITE_LANGS.includes(lang) ? lang : 'en';
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll);
    };
  }, [open]);

  return (
    <div className={`vani-lang-switch${open ? ' is-open' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className="vani-lang-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t.langLabel}
        onClick={() => setOpen((next) => !next)}
      >
        <GlobeIcon />
        <span className="vani-lang-current">{SITE_LANG_CODES[currentLang] || 'EN'}</span>
        <ChevronIcon />
      </button>
      {open ? (
        <ul className="vani-lang-menu" role="listbox" aria-label={t.langLabel}>
          {SITE_LANGS.map((code) => {
            const selected = code === currentLang;
            const native = SITE_LANG_NAMES[code] || code;
            const hint = SITE_LANG_HINTS[code];
            return (
              <li key={code} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`vani-lang-option${selected ? ' is-on' : ''}`}
                  onClick={() => {
                    setLang(code);
                    setOpen(false);
                  }}
                >
                  <span className="vani-lang-code">{SITE_LANG_CODES[code] || code.toUpperCase()}</span>
                  <span className="vani-lang-native">{native}</span>
                  {hint && hint !== native ? <span className="vani-lang-hint">{hint}</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

export default function SiteNav({ homeHash = false }) {
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const hash = (id) => (homeHash ? `#${id}` : `/#${id}`);

  return (
    <nav className={`vani-site-nav${stuck ? ' stuck' : ''}`} aria-label="Primary">
      <div className="vani-nav-left">
        <BrandLogo className="vani-brand" size="md" />
      </div>
      <div className="vani-navlinks">
        <a href={hash('how')}>{t.how}</a>
        <a href={hash('services')}>{t.services}</a>
        <NavLink to="/cancer">{t.cancerGuides || 'Cancer Guides'}</NavLink>
        <NavLink to="/find-hospitals">{t.findHospitals}</NavLink>
        <NavLink to="/cost-calculator">{t.costCalculator}</NavLink>
        <NavLink to="/for-hospitals">{t.forHospitals}</NavLink>
        {isAuthenticated ? null : (
          <NavLink to="/authentication">{t.signIn}</NavLink>
        )}
      </div>
      <div className="vani-nav-actions">
        <LangSwitch />
        {isAuthenticated ? (
          <UserAccountMenu compact />
        ) : null}
        <a className="vani-btn vani-btn-primary" href={isAuthenticated ? appUrl('/') : '/authentication?redirect=%2Fapp'}>
          {t.openApp} <span className="disc">→</span>
        </a>
      </div>
    </nav>
  );
}
